import type { Command } from "../document/commands.ts";
import { applyLayoutCommand, removeMapCommand, type LayoutCommand } from "../document/layout-commands.ts";
import { canonicalPlace, type MapSide } from "../document/layout.ts";
import { sameJson } from "../document/same-json.ts";
import type { MapDocument, MapGroup, MapPlace, WorldDocument, WorldGrid } from "../document/types.ts";
import { hashWords } from "../random.ts";
import { waysTo, type Ways } from "./door-links.ts";
import { neighbourToGo } from "./edge-strips.ts";
import { DEFAULT_MAP_GROUP_LABEL } from "./adapter.ts";
import { cellNameOf, placeOfCell } from "./map-cells.ts";

/**
 * A world's maps in the editor (P1.6-T38, ADR-070's amendment "several maps", ADR-021's amendment): the
 * rules a new map's id, seed, name and size follow, what a removal takes with it, and `MapEdits`, the store's
 * `maps`, which lists, opens, creates, renames and removes maps, each change one command.
 */

/**
 * Most characters in a map's name.
 */
export const MAP_NAME_MOST = 48;

/**
 * Most tiles along either side of a new map.
 */
export const MAP_SIDE_MOST = 1024;

/**
 * One map as the Maps tab, the switcher and the Map menu list it.
 */
export interface MapSummary {
  readonly id: string;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly open: boolean;
}

type PlaceMapCommand = Extract<LayoutCommand, { readonly kind: "placeMap" }>;

/**
 * What a map's row in the Maps tab shows of its layout (ADR-109 decision 10): its place, the name of its cell
 * when the world has a grid and the place is a cell's corner, its region and whether it is a draft. A map with
 * no place or no region carries neither field.
 */
export interface MapLayoutView {
  readonly place?: MapPlace;
  readonly cell: string | null;
  readonly group?: MapGroup;
  readonly draft: boolean;
}

/**
 * What goes with a removed map, which the removal question names, and what leads to it from elsewhere (ADR-122
 * decision 2.6): the doors and the rooms' hosts that name it, which keep their data and turn into broken links.
 */
export interface MapRemoval {
  readonly id: string;
  readonly name: string;
  readonly layers: number;
  readonly props: number;
  readonly markers: number;
  readonly ways: Ways;
}

/**
 * The id a new map takes: `map-<n>`, the least `n` from 2 up that no map of the world holds. The author never
 * types it, since it names the map's chunk directories and later a transition's target. In a tab that mints
 * its ids under a tag (ADR-086 decision 9 and ruling d of the amendment of the bounds) it is
 * `map-<tag>-<n>`, the least `n` from 1 up, so two tabs adding a map at once never mint one id.
 *
 * @param world The world.
 * @param tag The tab's tag, or null for an editor with none.
 * @returns The id.
 */
export function allocateMapId(world: WorldDocument, tag: string | null = null): string {
  const taken = new Set(world.maps.map((m) => m.id));
  for (let n = tag === null ? 2 : 1; ; n += 1) {
    const id = tag === null ? `map-${n}` : `map-${tag}-${n}`;
    if (!taken.has(id)) {
      return id;
    }
  }
}

/**
 * A new map's seed: the shared hash of the world's id and the map's, so every tool that makes the map again
 * draws the same one.
 *
 * @param worldId The world's id.
 * @param mapId The map's id.
 * @returns The seed, an unsigned 32-bit integer.
 */
export function newMapSeed(worldId: string, mapId: string): number {
  const text = `${worldId}/${mapId}`;
  const words = Array.from(text, (c) => c.charCodeAt(0));
  return hashWords(0, words, words.length);
}

/**
 * A new, empty map: no layers, groups or properties.
 *
 * @param worldId The world's id, for the seed.
 * @param mapId The map's id.
 * @param name The map's name.
 * @param width Width in tiles.
 * @param height Height in tiles.
 * @returns The map.
 */
export function newMapDocument(worldId: string, mapId: string, name: string, width: number, height: number): MapDocument {
  return { id: mapId, name, width, height, seed: newMapSeed(worldId, mapId), layers: [], properties: {} };
}

/**
 * Why a map may not take a name, or null when it may: the name, trimmed, is from 1 to `MAP_NAME_MOST`
 * characters and no other map of the world holds it, case aside.
 *
 * @param world The world.
 * @param name The name as typed.
 * @param mapId The map being renamed, whose own name does not count, or undefined for a new map.
 * @returns The reason, or null.
 */
export function mapNameRefusal(world: WorldDocument, name: string, mapId?: string): string | null {
  const trimmed = name.trim();
  if (trimmed === "") {
    return "Name the map";
  }
  if (trimmed.length > MAP_NAME_MOST) {
    return `A map's name is at most ${MAP_NAME_MOST} characters`;
  }
  const other = world.maps.find((m) => m.id !== mapId && m.name.trim().toLowerCase() === trimmed.toLowerCase());
  return other === undefined ? null : `Another map is named ${other.name}`;
}

/**
 * Why a new map may not take a size, or null when it may: each side a whole number of tiles from 1 to
 * `MAP_SIDE_MOST`.
 *
 * @param width Width in tiles.
 * @param height Height in tiles.
 * @returns The reason, or null.
 */
export function mapSizeRefusal(width: number, height: number): string | null {
  const side = (value: number): boolean => Number.isInteger(value) && value >= 1 && value <= MAP_SIDE_MOST;
  if (!side(width)) {
    return `Width is a whole number of tiles from 1 to ${MAP_SIDE_MOST}`;
  }
  return side(height) ? null : `Height is a whole number of tiles from 1 to ${MAP_SIDE_MOST}`;
}

/**
 * What goes with a map when it is removed.
 *
 * @param world The world, for what leads to the map.
 * @param map The map.
 * @returns Its name, its counts of layers, props and markers, and the doors and hosts that name it.
 */
export function mapRemoval(world: WorldDocument, map: MapDocument): MapRemoval {
  let props = 0;
  let markers = 0;
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      props += layer.props.length;
    } else if (layer.kind === "entity") {
      markers += layer.entities.length;
    }
  }
  return { id: map.id, name: map.name, layers: map.layers.length, props, markers, ways: waysTo(world, map.id) };
}

/**
 * What `MapEdits` needs from the store.
 */
export interface MapEditsHost {
  readonly document: () => WorldDocument;
  readonly mapId: () => string;
  readonly open: (mapId: string) => boolean;
  readonly forget: (mapId: string) => void;
  readonly run: (command: Command) => boolean;
  readonly startMap: () => string | undefined;
  /** The game's word for a map group, capitalised; "Map group" when the host gives none. */
  readonly groupLabel?: () => string;
  /** The tile at the middle of the view on the map in view, or null when not known. */
  readonly viewCentre?: () => { readonly x: number; readonly y: number } | null;
  /** Asks the camera to centre on a tile of the map about to be opened, or withdraws the ask. */
  readonly focus?: (tile: { readonly x: number; readonly y: number } | null) => void;
  /** The tag the tab mints its ids under, or null for an editor with none. */
  readonly idTag?: () => string | null;
}

/**
 * The store's maps: list, open, create, rename and remove, each change one command over the world.
 */
export class MapEdits {
  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: MapEditsHost) {}

  /**
   * Every map of the world, in the document's order, the one in view marked open.
   *
   * @returns The maps.
   */
  public list(): MapSummary[] {
    const open = this.host.mapId();
    return this.host.document().maps.map((m) => ({ id: m.id, name: m.name, width: m.width, height: m.height, open: m.id === open }));
  }

  /**
   * Opens a map. View state: it enters no history.
   *
   * @param mapId The map.
   * @returns False when it is already in view or a stroke is held.
   * @throws Error when the world has no such map.
   */
  public open(mapId: string): boolean {
    this.require(mapId);
    return this.host.open(mapId);
  }

  /**
   * Why Go to neighbour has nowhere to go on a side of the map in view, or null when a placed map shares an
   * edge there.
   *
   * @param side The side.
   * @returns The reason, or null.
   */
  public neighbourRefusal(side: MapSide): string | null {
    return neighbourToGo(this.host.document(), this.host.mapId(), side, 0) === null ? "No map on this side" : null;
  }

  /**
   * Opens the map sharing a side of the map in view and has the camera land on its facing edge at the same
   * row or column the view centred on (the middle of the side when the view's centre is not known). View
   * state: it enters no history.
   *
   * @param side The side to go through.
   * @param position Where along the side to land, in the map's own tiles, when the caller knows (a click on a
   *   strip); the view's centre when absent.
   * @returns False when the side has no neighbour, a stroke is held or the neighbour is already in view.
   */
  public goToNeighbour(side: MapSide, position?: number): boolean {
    const document = this.host.document();
    const map = this.require(this.host.mapId());
    const centre = this.host.viewCentre?.() ?? null;
    const along = position ?? (side === "north" || side === "south" ? (centre?.x ?? map.width / 2) : (centre?.y ?? map.height / 2));
    const target = neighbourToGo(document, map.id, side, along);
    if (target === null) {
      return false;
    }
    this.host.focus?.({ x: target.x, y: target.y });
    if (!this.host.open(target.map)) {
      this.host.focus?.(null);
      return false;
    }
    return true;
  }

  /**
   * Why a map may not take a name, or null.
   *
   * @param name The name as typed.
   * @param mapId The map being renamed, or undefined for a new map.
   * @returns The reason, or null.
   */
  public nameRefusal(name: string, mapId?: string): string | null {
    return mapNameRefusal(this.host.document(), name, mapId);
  }

  /**
   * Why a new map may not take a size, or null.
   *
   * @param width Width in tiles.
   * @param height Height in tiles.
   * @returns The reason, or null.
   */
  public sizeRefusal(width: number, height: number): string | null {
    return mapSizeRefusal(width, height);
  }

  /**
   * Adds an empty map after the others as one command and opens it, as a draft until a builder marks it ready
   * (ADR-109 decision 9). The view state the session kept for a removed map of the same id is forgotten first,
   * so the new map inherits no lock, hidden layer or active layer.
   *
   * @param name The name, trimmed.
   * @param width Width in tiles.
   * @param height Height in tiles.
   * @returns The new map's id.
   * @throws Error with the refusal's words when the name or size is refused.
   */
  public create(name: string, width: number, height: number): string {
    const document = this.host.document();
    const refusal = mapNameRefusal(document, name) ?? mapSizeRefusal(width, height);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    const id = allocateMapId(document, this.host.idTag?.() ?? null);
    this.host.forget(id);
    if (!this.host.run({ kind: "addMap", index: document.maps.length, map: { ...newMapDocument(document.id, id, name.trim(), width, height), draft: true } })) {
      throw new Error("the map was not added");
    }
    this.host.open(id);
    return id;
  }

  /**
   * Renames a map as one command, its id kept.
   *
   * @param mapId The map.
   * @param name The new name, trimmed.
   * @returns False when the name is the one it has, and nothing was recorded.
   * @throws Error when the world has no such map, or with the refusal's words when the name is refused.
   */
  public rename(mapId: string, name: string): boolean {
    const map = this.require(mapId);
    const trimmed = name.trim();
    if (trimmed === map.name) {
      return false;
    }
    const refusal = mapNameRefusal(this.host.document(), trimmed, mapId);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    return this.host.run({ kind: "renameMap", mapId, before: map.name, after: trimmed });
  }

  /**
   * Why a map may not be removed, or null when it may: the world keeps at least one map, and never loses the
   * map the game starts players on.
   *
   * @param mapId The map.
   * @returns The reason, or null.
   */
  public removeRefusal(mapId: string): string | null {
    if (this.host.document().maps.length <= 1) {
      return "A world keeps at least one map";
    }
    return this.host.startMap() === mapId ? "The game starts players on this map" : null;
  }

  /**
   * What goes with a map when it is removed, for the question asked first.
   *
   * @param mapId The map.
   * @returns The removal.
   * @throws Error when the world has no such map.
   */
  public removal(mapId: string): MapRemoval {
    return mapRemoval(this.host.document(), this.require(mapId));
  }

  /**
   * Removes a map with every seam naming it as one command that one undo takes back whole (ADR-109 decision
   * 6). The map in view is left first for the one before it, or the next when it is first.
   *
   * @param mapId The map.
   * @returns True once removed.
   * @throws Error when the world has no such map, or with the refusal's words when it may not be removed.
   */
  public remove(mapId: string): boolean {
    const map = this.require(mapId);
    const refusal = this.removeRefusal(mapId);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    const document = this.host.document();
    const index = document.maps.indexOf(map);
    if (this.host.mapId() === mapId) {
      const neighbour = neighbourOf(document, index);
      if (!this.host.open(neighbour)) {
        return false;
      }
    }
    return this.host.run(removeMapCommand(document, map.id));
  }

  /**
   * What a map's row shows of its layout.
   *
   * @param mapId The map.
   * @returns Its place, cell, region and draft mark.
   * @throws Error when the world has no such map.
   */
  public layout(mapId: string): MapLayoutView {
    const document = this.host.document();
    const map = this.require(mapId);
    const group = document.mapGroups?.find((g) => g.id === map.mapGroup);
    return {
      ...(map.place === undefined ? {} : { place: map.place }),
      cell: cellNameOf(document.grid, map.place),
      ...(group === undefined ? {} : { group }),
      draft: map.draft === true,
    };
  }

  /**
   * The world's regions, in their order.
   *
   * @returns The map groups.
   */
  public groups(): readonly MapGroup[] {
    return this.host.document().mapGroups ?? [];
  }

  /**
   * The world's grid.
   *
   * @returns The grid, or undefined when the world has none.
   */
  public grid(): WorldGrid | undefined {
    return this.host.document().grid;
  }

  /**
   * The place a cell name stands for on the world's grid.
   *
   * @param text The cell as typed.
   * @returns The place, or why the text names none.
   */
  public placeFromCell(text: string): { readonly place: { readonly x: number; readonly y: number } } | { readonly refusal: string } {
    const grid = this.host.document().grid;
    return grid === undefined ? { refusal: "This world has no grid; place the map by tiles" } : placeOfCell(grid, text);
  }

  /**
   * Why a map may not take a place, or null: the rule of `validateDocument` it would break, without the map's
   * id, or the seams it would leave without a shared edge. Nothing is recorded.
   *
   * @param mapId The map.
   * @param place The place, or undefined to take the map off the grid. A map's depth is kept.
   * @returns The reason, or null.
   * @throws Error when the world has no such map.
   */
  public placeRefusal(mapId: string, place: { readonly x: number; readonly y: number } | undefined): string | null {
    try {
      applyLayoutCommand(this.host.document(), this.placeCommand(mapId, place));
      return null;
    } catch (error) {
      const words = error instanceof Error ? error.message : String(error);
      return words.startsWith(`map ${mapId}: `) ? words.slice(`map ${mapId}: `.length) : words;
    }
  }

  /**
   * Places a map, or takes it off the grid, as one command.
   *
   * @param mapId The map.
   * @param place The place in tiles, or undefined to take the map off the grid. A map's depth is kept.
   * @returns False when the map already stands there, and nothing was recorded.
   * @throws Error when the world has no such map, or with the refusal's words when the place is refused.
   */
  public place(mapId: string, place: { readonly x: number; readonly y: number } | undefined): boolean {
    const command = this.placeCommand(mapId, place);
    if (sameJson(canonicalPlace(command.before), canonicalPlace(command.after))) {
      return false;
    }
    const refusal = this.placeRefusal(mapId, place);
    if (refusal !== null) {
      throw new Error(refusal);
    }
    return this.host.run(command);
  }

  /**
   * The game's word for a map group, as the Maps tab labels it.
   *
   * @returns The label, capitalised: "Map group" for a game with none.
   */
  public groupLabel(): string {
    return this.host.groupLabel?.() ?? DEFAULT_MAP_GROUP_LABEL;
  }

  /**
   * The game's word for a map group in a sentence.
   *
   * @returns The label in lower case.
   */
  public groupWord(): string {
    return this.groupLabel().toLowerCase();
  }

  /**
   * Puts a map in one of the world's map groups, or in none, as one command.
   *
   * @param mapId The map.
   * @param groupId The map group's id, or undefined for none.
   * @returns False when the map is already there, and nothing was recorded.
   * @throws Error when the world has no such map, or when the map group is not one of the world's.
   */
  public setGroup(mapId: string, groupId: string | undefined): boolean {
    const map = this.require(mapId);
    if (map.mapGroup === groupId) {
      return false;
    }
    if (groupId !== undefined && !this.groups().some((g) => g.id === groupId)) {
      throw new Error(`The world has no ${this.groupWord()} ${groupId}`);
    }
    return this.host.run({ kind: "setMapGroup", mapId, ...(map.mapGroup === undefined ? {} : { before: map.mapGroup }), ...(groupId === undefined ? {} : { after: groupId }) });
  }

  /**
   * Marks a map a draft or ready as one command (ADR-109 decision 9).
   *
   * @param mapId The map.
   * @param draft True for a draft, false for ready.
   * @returns False when the map already has the mark, and nothing was recorded.
   * @throws Error when the world has no such map.
   */
  public setDraft(mapId: string, draft: boolean): boolean {
    const map = this.require(mapId);
    if ((map.draft === true) === draft) {
      return false;
    }
    return this.host.run({ kind: "setMapDraft", mapId, before: !draft, after: draft });
  }

  /**
   * The command that moves a map to a place, keeping the depth it has.
   *
   * @param mapId The map.
   * @param place The place, or undefined for none.
   * @returns The `placeMap` command.
   * @throws Error when the world has no such map.
   */
  private placeCommand(mapId: string, place: { readonly x: number; readonly y: number } | undefined): PlaceMapCommand {
    const map = this.require(mapId);
    const after = place === undefined ? undefined : canonicalPlace({ x: place.x, y: place.y, ...(map.place?.depth === undefined ? {} : { depth: map.place.depth }) });
    return { kind: "placeMap", mapId, ...(map.place === undefined ? {} : { before: map.place }), ...(after === undefined ? {} : { after }) };
  }

  /**
   * The map of an id.
   *
   * @param mapId The map.
   * @returns The map.
   * @throws Error when the world has no such map.
   */
  private require(mapId: string): MapDocument {
    const map = this.host.document().maps.find((m) => m.id === mapId);
    if (map === undefined) {
      throw new Error(`the world has no map ${mapId}`);
    }
    return map;
  }
}

/**
 * The map opened when the map at an index leaves the view for good: the one before it, or the next when it
 * is first.
 *
 * @param world The world, still holding the map.
 * @param index The leaving map's index.
 * @returns The neighbour's id.
 * @throws Error when the world holds no other map.
 */
export function neighbourOf(world: WorldDocument, index: number): string {
  const neighbour = world.maps[index > 0 ? index - 1 : index + 1];
  if (neighbour === undefined) {
    throw new Error("the world holds no other map to open");
  }
  return neighbour.id;
}
